import React, { useState, useEffect, useRef } from "react";
import { X, Send, MessageCircle, Minimize2, Maximize2, Loader2, RefreshCw } from "lucide-react";
import axios from "axios";
import { io } from "socket.io-client";

// Use environment variable or default to production
const API_BASE = "https://app.commercialcleaningleadsforyou.com";
const WS_BASE = "https://app.commercialcleaningleadsforyou.com";

const STORAGE_KEY = "chat_user_email";
const STORAGE_PHONE_KEY = "chat_user_phone";

/** Display-only: hide @domain and capitalize first letter (sreehari@… → Sreehari) */
function displayEmailName(email) {
  if (!email) return "";
  const name = String(email).split("@")[0];
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1);
}
const STORAGE_EXPIRY = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

const ChatWidget = ({ onOpenChange, isExternallyOpened = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [ipDetails, setIpDetails] = useState(null);
  const [rateLimitStatus, setRateLimitStatus] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [conversation, setConversation] = useState([]);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [socket, setSocket] = useState(null);
  const [viewMode, setViewMode] = useState("form"); // "form" | "chat"
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastReadMessageId, setLastReadMessageId] = useState(null);
  const messagesEndRef = useRef(null);

  // Load saved email and phone from localStorage on mount (but don't auto-open)
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { email: savedEmail, timestamp } = JSON.parse(saved);
        const now = Date.now();
        if (now - timestamp < STORAGE_EXPIRY) {
          setEmail(savedEmail);
          // Load conversation in background but don't auto-open widget
          // This allows unread count to show without opening chat
          fetchConversation(savedEmail);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
      
      // Load saved phone number
      const savedPhone = localStorage.getItem(STORAGE_PHONE_KEY);
      if (savedPhone) {
        const { phone: savedPhoneValue, timestamp: phoneTimestamp } = JSON.parse(savedPhone);
        const now = Date.now();
        if (now - phoneTimestamp < STORAGE_EXPIRY) {
          setPhone(savedPhoneValue);
        } else {
          localStorage.removeItem(STORAGE_PHONE_KEY);
        }
      }
    } catch (error) {
      console.error("Error loading saved email/phone:", error);
    }
  }, []);

  // Save email to localStorage when it changes
  useEffect(() => {
    if (email && email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(email)) {
        try {
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
              email: email.trim(),
              timestamp: Date.now(),
            })
          );
        } catch (error) {
          console.error("Error saving email:", error);
        }
      }
    }
  }, [email]);

  // Save phone to localStorage when it changes
  useEffect(() => {
    if (phone && phone.trim()) {
      try {
        localStorage.setItem(
          STORAGE_PHONE_KEY,
          JSON.stringify({
            phone: phone.trim(),
            timestamp: Date.now(),
          })
        );
      } catch (error) {
        console.error("Error saving phone:", error);
      }
    }
  }, [phone]);

  // Initialize WebSocket connection - Keep connected even when widget is closed to receive admin replies
  useEffect(() => {
    if (email && email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return;
      }

      const newSocket = io(WS_BASE, {
        transports: ["websocket", "polling"],
      });

      newSocket.on("connect", () => {
        console.log("Connected to WebSocket server");
        newSocket.emit("join-conversation", email.trim());
      });

      newSocket.on("disconnect", () => {
        console.log("Disconnected from WebSocket server");
      });

      // Listen for admin replies
      newSocket.on("admin-reply", (data) => {
        console.log("Received admin reply:", data);
        if (data.email === email.trim()) {
          // Update conversation if it exists, otherwise fetch it
          setConversation((prev) => {
            // If conversation is empty, fetch it (will be called outside setState)
            const isEmpty = prev.length === 0;
            if (isEmpty) {
              // Fetch conversation to get all messages with the new reply
              setTimeout(() => {
                fetchConversation(email.trim());
              }, 0);
              return prev;
            }
            // Otherwise update the existing conversation
            return prev.map((msg) =>
              msg.id === data.messageId
                ? {
                    ...msg,
                    admin_reply: data.reply, // Keep for backward compatibility
                    admin_replied_at: data.repliedAt,
                    admin_replies: [
                      ...(msg.admin_replies || []),
                      {
                        id: data.replyId,
                        message_id: data.messageId,
                        reply: data.reply,
                        created_at: data.repliedAt,
                      },
                    ],
                  }
                : msg
            );
          });
          
          // Check if this is a new unread message
          // Increment unread count if message is after last read
          // The count will be cleared when user opens and views the chat
          setLastReadMessageId((prevLastRead) => {
            const isAfterLastRead = !prevLastRead || data.messageId > prevLastRead;
            if (isAfterLastRead) {
              setUnreadCount((prevCount) => prevCount + 1);
            }
            return prevLastRead; // Don't update lastReadMessageId here - it's updated when viewing
          });
        }
      });

      // Listen for new messages
      newSocket.on("new-message", (data) => {
        if (data.email === email.trim()) {
          setConversation((prev) => {
            if (!prev.find((msg) => msg.id === data.id)) {
              return [...prev, data];
            }
            return prev;
          });
        }
      });

      setSocket(newSocket);

      return () => {
        newSocket.disconnect();
      };
    }
  }, [email]);

  // Join conversation room when socket connects and email is available
  useEffect(() => {
    if (socket && socket.connected && email && email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(email)) {
        socket.emit("join-conversation", email.trim());
        // Only fetch conversation if widget is open or if we don't have conversation data
        if (isOpen || conversation.length === 0) {
          fetchConversation(email.trim());
        }
      }
    }
  }, [socket, email, isOpen]);

  // Scroll to bottom when conversation updates
  useEffect(() => {
    if (viewMode === "chat" && messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [conversation, viewMode]);

  // Mark messages as read when conversation is viewed
  useEffect(() => {
    if (viewMode === "chat" && conversation.length > 0 && isOpen) {
      const latestMessageId = Math.max(...conversation.map((msg) => msg.id));
      if (latestMessageId !== lastReadMessageId) {
        setLastReadMessageId(latestMessageId);
        setUnreadCount(0);
        // Save to localStorage
        if (email) {
          try {
            localStorage.setItem(`chat_last_read_${email}`, latestMessageId.toString());
          } catch (error) {
            console.error("Error saving last read:", error);
          }
        }
      }
    }
  }, [viewMode, conversation, lastReadMessageId, isOpen, email]);

  // Fetch IP details when widget opens
  useEffect(() => {
    if (isOpen && !ipDetails) {
      const fetchIpDetails = async () => {
        try {
          const response = await axios.get("https://ipinfo.io/json");
          setIpDetails(response.data);
        } catch (error) {
          console.error("Error fetching IP details:", error);
        }
      };
      fetchIpDetails();
    }
  }, [isOpen]);

  // Check rate limit when email changes or conversation updates
  useEffect(() => {
    if (email && email.trim()) {
      checkRateLimit();
    }
  }, [email, conversation]);

  // Fetch conversation history
  const fetchConversation = async (emailAddress) => {
    if (!emailAddress) return;
    
    setLoadingConversation(true);
    try {
      const response = await axios.get(
        `${API_BASE}/api/chat/conversation/${encodeURIComponent(emailAddress)}`
      );
      if (response.data?.data) {
        const messages = response.data.data;
        setConversation(messages);
        if (messages.length > 0) {
          setViewMode("chat");
          // Load last read message ID from localStorage
          const lastReadKey = `chat_last_read_${emailAddress}`;
          const savedLastRead = localStorage.getItem(lastReadKey);
          const savedLastReadId = savedLastRead ? parseInt(savedLastRead, 10) : null;
          
          // Set last read message ID (use saved or latest)
          const latestId = Math.max(...messages.map((msg) => msg.id));
          const currentLastRead = savedLastReadId || latestId;
          setLastReadMessageId(currentLastRead);
          
          // Check for unread admin replies (messages with admin_reply after last read)
          const unreadReplies = messages.filter(
            (msg) => msg.admin_reply && msg.admin_replied_at && (!currentLastRead || msg.id > currentLastRead)
          );
          setUnreadCount(unreadReplies.length);
        }
      }
    } catch (error) {
      console.error("Error fetching conversation:", error);
    } finally {
      setLoadingConversation(false);
    }
  };

  // Check rate limit status
  const checkRateLimit = async () => {
    if (!email || !email.trim()) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/chat/check-rate-limit?email=${encodeURIComponent(email.trim())}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        const data = await response.json();
        setRateLimitStatus(data);
        if (data.blocked && data.remainingTime > 0) {
          startCountdown(data.remainingTime);
        }
      }
    } catch (error) {
      console.error("Error checking rate limit:", error);
    }
  };

  // Countdown timer for rate limit
  const startCountdown = (initialSeconds) => {
    let seconds = initialSeconds;
    setRemainingTime(seconds);

    const interval = setInterval(() => {
      seconds -= 1;
      setRemainingTime(seconds);

      if (seconds <= 0) {
        clearInterval(interval);
        checkRateLimit();
      }
    }, 1000);

    return () => clearInterval(interval);
  };

  // Format remaining time
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    // Check if user is from US (only check if we have IP details)
    if (countryChecked && !isUSUser) {
      setError("Sorry, this service is currently only available for users from the United States.");
      return;
    }

    if (!email.trim()) {
      setError("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!message.trim()) {
      setError("Message is required");
      return;
    }

    if (rateLimitStatus?.blocked) {
      setError(
        `You have reached the message limit. Please try again in ${formatTime(remainingTime)}.`
      );
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Use provided name or fallback to email prefix
      const userName = name.trim() || email.trim().split('@')[0];
      
      const response = await fetch(`${API_BASE}/api/chat/submit-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: userName,
          email: email.trim(),
          phone: phone.trim() || null,
          message: message.trim(),
          ipDetails,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message");
      }

      // Update rate limit status with response data
      if (data.showWarning !== undefined || data.remainingAttempts !== undefined) {
        setRateLimitStatus({
          ...rateLimitStatus,
          showWarning: data.showWarning,
          remainingAttempts: data.remainingAttempts,
          totalAttempts: data.totalAttempts,
          maxAttempts: data.maxAttempts,
          warningThreshold: data.warningThreshold,
        });
      }

      const newMessage = {
        id: data.id,
        email: email.trim(),
        message: message.trim(),
        created_at: data.createdAt,
        admin_reply: null,
        admin_replied_at: null,
      };
      setConversation((prev) => [...prev, newMessage]);
      setViewMode("chat");
      setMessage("");
      setSuccess(true);
      
      setTimeout(() => {
        checkRateLimit();
        setSuccess(false);
      }, 2000);

      setTimeout(() => {
        fetchConversation(email.trim());
      }, 500);
    } catch (err) {
      setError(err.message || "Failed to send message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle external open state
  useEffect(() => {
    if (isExternallyOpened) {
      setIsOpen(true);
    }
  }, [isExternallyOpened]);

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
    // Don't clear email - keep it for next time
    setMessage("");
    setError("");
    setSuccess(false);
    setRateLimitStatus(null);
    setRemainingTime(0);
    setConversation([]);
    setViewMode("form");
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    if (onOpenChange) {
      onOpenChange(true);
    }
  };

  const isBlocked = rateLimitStatus?.blocked && remainingTime > 0;
  const hasConversation = conversation.length > 0;
  const isFirstTime = viewMode === "form" && !hasConversation;
  const isUSUser = ipDetails?.country === "US";
  const countryChecked = ipDetails !== null; // Whether we've received IP details

  return (
    <>
      {/* Floating Chat Button - Always show when chat is closed, fixed position during scroll */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="chat-widget-button fixed bottom-6 right-6 z-[9999] w-16 h-16 bg-[#26A94A] text-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center group relative"
          aria-label="Open chat"
          style={{ 
            boxShadow: '0 10px 40px rgba(38, 169, 74, 0.4)',
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9999,
            transform: 'translateZ(0)', // Force hardware acceleration
          }}
        >
          <MessageCircle size={28} className="group-hover:scale-110 transition-transform" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[24px] h-6 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold text-white animate-pulse border-2 border-white px-1.5">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          {email && hasConversation && unreadCount === 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full border-2 border-white"></span>
          )}
          {/* Default indicator - show a subtle dot to indicate chat is available */}
          {!email && unreadCount === 0 && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-white/80 rounded-full border border-gray-300"></span>
          )}
        </button>
      )}

      {/* Chat Widget */}
      {isOpen && (
        <div
          className={`chat-widget-container fixed bottom-6 right-6 z-[9999] bg-white rounded-2xl shadow-2xl transition-all duration-300 ${
            isMinimized 
              ? "w-80 h-16" 
              : isFirstTime 
                ? "w-96 h-[420px]" 
                : "w-96 h-[600px]"
          } flex flex-col overflow-hidden border border-gray-200`}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9999,
            transform: 'translateZ(0)', // Force hardware acceleration
          }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1465B7] to-[#26A94A] text-white p-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <MessageCircle size={20} />
              <h3 className="font-semibold">
                {viewMode === "chat" ? "Chat" : "Send a Message, We’ll Get Back to You Soon!"}
              </h3>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {viewMode === "chat" && email && (
                <button
                  onClick={async () => {
                    if (email.trim()) {
                      await fetchConversation(email.trim());
                    }
                  }}
                  className="p-1.5 hover:bg-white/20 rounded transition-colors"
                  aria-label="Refresh conversation"
                  title="Refresh conversation"
                >
                  <RefreshCw size={16} />
                </button>
              )}
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                aria-label={isMinimized ? "Maximize" : "Minimize"}
              >
                {isMinimized ? <Maximize2 size={18} /> : <Minimize2 size={18} />}
              </button>
              <button
                onClick={handleClose}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              {/* Conversation View - ChatGPT Style */}
              {viewMode === "chat" && (
                <>
                  <div className="flex-1 overflow-y-auto bg-white chat-widget-scroll min-h-0">
                    {loadingConversation ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 animate-spin text-[#1465B7]" />
                      </div>
                    ) : conversation.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">No messages yet</div>
                    ) : (
                      <div className="py-4">
                        {conversation.map((msg, index) => (
                          <div key={msg.id} className="space-y-3">
                            {/* User Message or Admin-Created Message - Right Side */}
                            <div className="flex justify-end px-4 my-4">
                              <div className="flex items-start gap-3 max-w-[85%]">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-end gap-2 mb-1">
                                    <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                                    <span className="text-sm font-medium text-gray-900">
                                      {msg.user_ip === 'admin-created' || msg.name?.startsWith('[Admin]')
                                        ? (msg.name?.replace('[Admin] ', '') || 'Admin')
                                        : displayEmailName(msg.email)}
                                    </span>
                                    {(msg.user_ip === 'admin-created' || msg.name?.startsWith('[Admin]')) && (
                                      <span className="text-xs bg-[#26A94A] text-white px-2 py-0.5 rounded">Admin</span>
                                    )}
                                  </div>
                                  <div className={`rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                                    msg.user_ip === 'admin-created' || msg.name?.startsWith('[Admin]')
                                      ? 'bg-[#26A94A] text-white border-2 border-[#26A94A]'
                                      : 'bg-[#1465B7] text-white'
                                  }`}>
                                    {msg.message}
                                  </div>
                                </div>
                                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold ${
                                  msg.user_ip === 'admin-created' || msg.name?.startsWith('[Admin]')
                                    ? 'bg-[#26A94A]'
                                    : 'bg-[#1465B7]'
                                }`}>
                                  {msg.user_ip === 'admin-created' || msg.name?.startsWith('[Admin]')
                                    ? 'A'
                                    : displayEmailName(msg.email).charAt(0).toUpperCase()}
                                </div>
                              </div>
                            </div>

                            {/* Admin Reply - Left Side */}
                            {msg.admin_reply && (
                              <div className="flex justify-start px-4">
                                <div className="flex items-start gap-3 max-w-[85%]">
                                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#26A94A] flex items-center justify-center text-white text-xs font-semibold">
                                    A
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-sm font-medium text-gray-900">Admin</span>
                                      <span className="text-xs text-gray-400">{formatDate(msg.admin_replied_at)}</span>
                                    </div>
                                    <div className="bg-gray-100 text-gray-800 rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed border border-gray-200">
                                      {msg.admin_reply}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </div>

                  {/* Message Input - ChatGPT Style */}
                  <div className="border-t border-gray-200 bg-white p-3 flex-shrink-0">
                    {/* Country Restriction Message */}
                    {countryChecked && !isUSUser && (
                      <div className="mb-3 bg-yellow-50 border border-yellow-200 rounded-lg p-2.5">
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-yellow-800 mb-0.5">
                              Service Unavailable
                            </p>
                            <p className="text-xs text-yellow-700">
                              This service is currently only available for users from the United States.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Warning when user has sent 3+ messages without admin reply */}
                    {rateLimitStatus?.showWarning && !isBlocked && (
                      <div className="mb-3 bg-amber-50 border border-amber-300 rounded-lg p-2.5">
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-amber-800 mb-0.5">
                              Waiting for Admin Response
                            </p>
                            <p className="text-xs text-amber-700">
                              {rateLimitStatus.totalAttempts} message{rateLimitStatus.totalAttempts !== 1 ? 's' : ''} sent. 
                              {rateLimitStatus.remainingAttempts > 0 ? (
                                <> <strong>{rateLimitStatus.remainingAttempts}</strong> remaining.</>
                              ) : (
                                <> Please wait for a reply.</>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    <form onSubmit={handleSubmit} className="flex gap-2 items-end">
                      <div className="flex-1 relative">
                        <textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Type your message..."
                          rows={1}
                          className="w-full px-4 py-2.5 pr-12 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1465B7] focus:border-transparent resize-none max-h-32 overflow-y-auto"
                          disabled={isBlocked || isLoading || !isUSUser}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              if (!isLoading && message.trim()) {
                                handleSubmit(e);
                              }
                            }
                          }}
                        />
                        {isLoading && (
                          <div className="absolute right-3 bottom-3">
                            <Loader2 className="w-4 h-4 animate-spin text-[#1465B7]" />
                          </div>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={isBlocked || isLoading || !message.trim() || !isUSUser}
                        className={`px-4 py-2.5 rounded-lg font-semibold text-white text-sm transition-colors flex items-center justify-center min-w-[44px] ${
                          isBlocked || isLoading || !message.trim() || !isUSUser
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-[#1465B7] hover:bg-[#0f4d8a] cursor-pointer"
                        }`}
                      >
                        {isLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send size={18} />
                        )}
                      </button>
                    </form>
                    {error && (
                      <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2">
                        <p className="text-xs text-red-800">{error}</p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Form View - Compact for First Time */}
              {viewMode === "form" && (
                <div className="p-4 overflow-y-auto chat-widget-scroll flex-1 min-h-0">
                  {success ? (
                    <div className="text-center py-6">
                      <div className="mb-3">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100">
                          <svg
                            className="w-6 h-6 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      </div>
                      <h3 className="text-base font-semibold text-green-600 mb-1">Message Sent!</h3>
                      <p className="text-xs text-gray-600">
                        Thank you for contacting us. We'll get back to you soon.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-3">
                      {isBlocked && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                          <p className="text-xs text-yellow-800">
                            <strong>Rate Limit:</strong> Please try again in{" "}
                            <strong>{formatTime(remainingTime)}</strong>.
                          </p>
                        </div>
                      )}

                      {/* Warning when user has sent 3+ messages without admin reply */}
                      {rateLimitStatus?.showWarning && !isBlocked && (
                        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-amber-800 mb-1">
                                Waiting for Admin Response
                              </p>
                              <p className="text-xs text-amber-700">
                                You've sent {rateLimitStatus.totalAttempts} message{rateLimitStatus.totalAttempts !== 1 ? 's' : ''} without a reply. 
                                {rateLimitStatus.remainingAttempts > 0 ? (
                                  <> You have <strong>{rateLimitStatus.remainingAttempts}</strong> message{rateLimitStatus.remainingAttempts !== 1 ? 's' : ''} remaining.</>
                                ) : (
                                  <> Please wait for an admin response to continue.</>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {rateLimitStatus && !isBlocked && !rateLimitStatus.showWarning && rateLimitStatus.remainingAttempts <= 3 && rateLimitStatus.remainingAttempts > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                          <p className="text-xs text-blue-800">
                            <strong>Remaining attempts:</strong> {rateLimitStatus.remainingAttempts} of 5
                          </p>
                        </div>
                      )}

                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                          <p className="text-xs text-red-800">{error}</p>
                        </div>
                      )}

                      {/* Name Field */}
                      <div>
                        <label
                          htmlFor="chat-name"
                          className="block text-xs font-medium text-gray-700 mb-1"
                        >
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="chat-name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1465B7] focus:border-transparent"
                          placeholder="Your name"
                          required
                          disabled={isBlocked || isLoading}
                        />
                      </div>

                      {/* Email Field */}
                      <div>
                        <label
                          htmlFor="chat-email"
                          className="block text-xs font-medium text-gray-700 mb-1"
                        >
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="chat-email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1465B7] focus:border-transparent"
                          placeholder="your@email.com"
                          required
                          disabled={isBlocked || isLoading}
                        />
                      </div>

                      {/* Phone Field */}
                      <div>
                        <label
                          htmlFor="chat-phone"
                          className="block text-xs font-medium text-gray-700 mb-1"
                        >
                          Phone
                        </label>
                        <input
                          type="tel"
                          id="chat-phone"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1465B7] focus:border-transparent"
                          placeholder="Your phone number (optional)"
                          disabled={isBlocked || isLoading}
                        />
                      </div>

                      {/* Message Field */}
                      <div>
                        <label
                          htmlFor="chat-message"
                          className="block text-xs font-medium text-gray-700 mb-1"
                        >
                          Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          id="chat-message"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1465B7] focus:border-transparent resize-none"
                          placeholder="Type your message here..."
                          required
                          disabled={isBlocked || isLoading}
                        />
                      </div>

                      {/* Country Restriction Message */}
                      {countryChecked && !isUSUser && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <p className="text-xs text-yellow-800">
                            <strong>Service Unavailable:</strong> This service is currently only available for users from the United States.
                          </p>
                        </div>
                      )}

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={isBlocked || isLoading || !isUSUser}
                        className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm text-white transition-colors flex items-center justify-center gap-2 ${
                          isBlocked || isLoading || !isUSUser
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-[#1465B7] hover:bg-[#0f4d8a] active:bg-[#0d3f70] cursor-pointer"
                        }`}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Sending...</span>
                          </>
                        ) : (
                          "Send Message"
                        )}
                      </button>

                      <p className="text-xs text-gray-500 text-center">
                        * Your email will be used as your identifier
                      </p>
                    </form>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <style>{`
        .chat-widget-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .chat-widget-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        .chat-widget-scroll::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 3px;
        }
        .chat-widget-scroll::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </>
  );
};

export default ChatWidget;
